import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { INBOUND_REFERRALS_FORM_KEY } from 'components/notificationPreferences/InboundReferralPreferencesContainer';
import { InboundReferralSection } from 'components/notificationPreferences/inboundReferralSections';
import { PreferencesFormMeta, PreferencesFormValues } from 'components/notificationPreferences/NotificationPreferences';
import Checkbox from 'components/shared/form/Checkbox';
import Toggle from 'components/shared/Toggle';
import { Flags } from 'constants/flags';
import { useForm } from 'context/form';
import { useProfile } from 'context/profile';
import { InboundReferralPreference, NotificationChannel, NotificationChannels } from 'models/Notifications';
import colors from 'styles/theme/colors';
import { BodySmall, BodySmallBold, Label } from 'styles/typography';
import ChevronDownBold from 'svg/ChevronDownBold';
import ChevronRightBold from 'svg/ChevronRightBold';

type Props = {
  section: InboundReferralSection;
  disabled?: boolean;
  // The General row's sections render through this component too, under their own form key.
  formKey?: string;
  testIdPrefix?: string;
};

// One Inbound Referrals page section — a stored category with its own active master, exactly
// like a care-coordination category card, but registry-driven: the rows are the section's real
// kinds with display labels and captions, not the humanized stored keys. Channels: email always,
// the In-App column whenever the user carries the in_app_notifications flag (OLIO-3231), and the
// Push column under push_notifications — the same per-user gates the care-coordination cards read.
//
// Deliberately still a sibling of NotificationCategoryPreferences rather than a parameterization
// of it: the dual-channel divergence is gone now that in-app landed, but the remaining ones
// (registry labels/captions vs humanize+translations, this form key's preference shape vs the
// tab-keyed one) would each become a prop for exactly two call sites. Converge if a third
// caller appears.
export default function InboundReferralSectionPreferences({
  section,
  disabled = false,
  formKey = INBOUND_REFERRALS_FORM_KEY,
  testIdPrefix = 'inbound-referral-section',
}: Props) {
  const { values, setValue } = useForm<PreferencesFormValues, PreferencesFormMeta>();
  const { profile } = useProfile();
  const [expanded, setExpanded] = useState(false);

  const showInApp = profile.hasFlag(Flags.InAppNotifications);
  const showPush = profile.hasFlag(Flags.PushNotifications);

  const preference = values[formKey] as InboundReferralPreference | undefined;
  const sectionValues = useMemo(
    () => preference?.notificationSelections?.[section.category] ?? {},
    [preference, section.category]
  );

  // `active` is the section's stored master switch, a sibling of the kind keys; absent = on.
  const sectionActive = sectionValues.active !== false;
  // Select-all over a single row would duplicate that row's own checkbox.
  const hasSelectAll = section.kinds.length > 1;

  const channelValue = (kind: string, channel: NotificationChannel) =>
    !!(sectionValues[kind] as Partial<NotificationChannels> | undefined)?.[channel];

  const _setPref = (kind: string, channel: NotificationChannel, val: boolean) => {
    setValue(`${formKey}.notificationSelections.${section.category}.${kind}.${channel}`, val);
  };

  const handleSelectAll = (val: boolean, channel: NotificationChannel) => {
    section.kinds.forEach(({ kind }) => _setPref(kind, channel, val));
  };

  // The master switch mutes the section's delivery without touching the checkbox selections,
  // which stay stored for when it comes back on.
  const handleSectionToggle = (val: boolean) => {
    setValue(`${formKey}.notificationSelections.${section.category}.active`, val);
  };

  const allEmailSelected = useMemo(
    () =>
      section.kinds.every(({ kind }) => !!(sectionValues[kind] as Partial<NotificationChannels> | undefined)?.email),
    [section.kinds, sectionValues]
  );

  const allInAppSelected = useMemo(
    () =>
      section.kinds.every(({ kind }) => !!(sectionValues[kind] as Partial<NotificationChannels> | undefined)?.inApp),
    [section.kinds, sectionValues]
  );

  const allPushSelected = useMemo(
    () => section.kinds.every(({ kind }) => !!(sectionValues[kind] as Partial<NotificationChannels> | undefined)?.push),
    [section.kinds, sectionValues]
  );

  return (
    <Card data-testid={`${testIdPrefix}-${section.category}`}>
      <CardHeader
        data-cy='clickable'
        role='button'
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((val) => !val)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded((val) => !val);
          }
        }}>
        <Chevron>{expanded ? <ChevronDownBold /> : <ChevronRightBold />}</Chevron>
        <HeaderText>
          <SectionTitle>{section.title}</SectionTitle>
          <SectionDescription>{section.description}</SectionDescription>
        </HeaderText>
        <ToggleContainer onClick={(event) => event.stopPropagation()}>
          <ToggleLabel>{sectionActive ? 'On' : 'Off'}</ToggleLabel>
          <Toggle active={sectionActive} disabled={disabled} onChange={handleSectionToggle} />
        </ToggleContainer>
      </CardHeader>

      {expanded && (
        <List $muted={!sectionActive}>
          <ListItem $compact={!hasSelectAll}>
            <Cell></Cell>
            {showPush && (
              <CheckboxContainer>
                <HeaderLabel>Push</HeaderLabel>
                {hasSelectAll && (
                  <Checkbox
                    key={`${section.category}-all-push`}
                    dataCyLabel='select-all-checkbox-push'
                    disabled={disabled || !sectionActive}
                    size={14}
                    color={colors.black100}
                    checkedColor={colors.blue100}
                    labelMargin='0px'
                    checked={allPushSelected}
                    onChange={(val) => handleSelectAll(val, NotificationChannel.PUSH)}
                  />
                )}
              </CheckboxContainer>
            )}
            {showInApp && (
              <CheckboxContainer>
                <HeaderLabel>In-App</HeaderLabel>
                {hasSelectAll && (
                  <Checkbox
                    key={`${section.category}-all-in-app`}
                    dataCyLabel='select-all-checkbox-in-app'
                    disabled={disabled || !sectionActive}
                    size={14}
                    color={colors.black100}
                    checkedColor={colors.blue100}
                    labelMargin='0px'
                    checked={allInAppSelected}
                    onChange={(val) => handleSelectAll(val, NotificationChannel.IN_APP)}
                  />
                )}
              </CheckboxContainer>
            )}
            <CheckboxContainer>
              <HeaderLabel>Email</HeaderLabel>
              {hasSelectAll && (
                <Checkbox
                  key={`${section.category}-all-email`}
                  dataCyLabel='select-all-checkbox-email'
                  disabled={disabled || !sectionActive}
                  size={14}
                  color={colors.black100}
                  checkedColor={colors.blue100}
                  labelMargin='0px'
                  checked={allEmailSelected}
                  onChange={(val) => handleSelectAll(val, NotificationChannel.EMAIL)}
                />
              )}
            </CheckboxContainer>
          </ListItem>

          {section.kinds.map(({ kind, label, caption }) => (
            <ListItem key={kind}>
              <Cell>
                <KindLabel>{label}</KindLabel>
                {caption && <KindCaption>{caption}</KindCaption>}
              </Cell>
              {showPush && (
                <CheckboxContainer>
                  <Checkbox
                    disabled={disabled || !sectionActive}
                    size={14}
                    color={colors.black100}
                    checkedColor={colors.blue100}
                    labelMargin='0px'
                    dataCyLabel={`checkbox-push-${kind}`}
                    checked={channelValue(kind, NotificationChannel.PUSH)}
                    onChange={(val) => _setPref(kind, NotificationChannel.PUSH, val)}
                  />
                </CheckboxContainer>
              )}
              {showInApp && (
                <CheckboxContainer>
                  <Checkbox
                    disabled={disabled || !sectionActive}
                    size={14}
                    color={colors.black100}
                    checkedColor={colors.blue100}
                    labelMargin='0px'
                    dataCyLabel={`checkbox-in-app-${kind}`}
                    checked={channelValue(kind, NotificationChannel.IN_APP)}
                    onChange={(val) => _setPref(kind, NotificationChannel.IN_APP, val)}
                  />
                </CheckboxContainer>
              )}
              <CheckboxContainer>
                <Checkbox
                  disabled={disabled || !sectionActive}
                  size={14}
                  color={colors.black100}
                  checkedColor={colors.blue100}
                  labelMargin='0px'
                  dataCyLabel={`checkbox-email-${kind}`}
                  checked={channelValue(kind, NotificationChannel.EMAIL)}
                  onChange={(val) => _setPref(kind, NotificationChannel.EMAIL, val)}
                />
              </CheckboxContainer>
            </ListItem>
          ))}
        </List>
      )}
    </Card>
  );
}

const Card = styled.div`
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  border: 1px solid ${({ theme }) => theme.colors.black10};
  background-color: ${({ theme }) => theme.colors.white};
  margin-bottom: 16px;
  padding: 16px 24px;
`;

const CardHeader = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  gap: 12px;
`;

const Chevron = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.black100};
  display: flex;
  flex-shrink: 0;
  width: 14px;
`;

const HeaderText = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled(BodySmallBold)`
  font-size: 14px;
`;

const SectionDescription = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black75};
  font-size: 12px;
`;

const ToggleContainer = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

const ToggleLabel = styled(Label)`
  color: ${({ theme }) => theme.colors.black75};
`;

const HeaderLabel = styled(Label)`
  color: ${({ theme }) => theme.colors.black75};
  margin-bottom: 10px;
`;

const KindLabel = styled(Label)`
  color: ${({ theme }) => theme.colors.black75};
  font-weight: 500;
`;

export const KindCaption = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black75};
  display: block;
  font-size: 11px;
  margin-top: 4px;
`;

const List = styled.div<{ $muted?: boolean }>`
  margin-top: 16px;
  opacity: ${({ $muted }) => ($muted ? 0.5 : 1)};
`;

// A header row without select-alls holds only the column labels, so it sits closer to the row.
const ListItem = styled.div<{ $compact?: boolean }>`
  display: flex;
  padding: ${({ $compact }) => ($compact ? '16px 0 10px' : '16px 0')};
  border-bottom: 1px solid ${(props) => props.theme.colors.black10};
  align-items: center;
  line-height: 16px;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const Cell = styled.div`
  min-width: 150px;
  flex: 1;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 48px;
  align-items: center;
  text-align: center;

  & + & {
    margin-left: 8px;
  }
`;
